import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import PageCard from 'components/page-card';
import { useMemo, useState } from 'react';
import { MdAdd, MdVpnKey } from 'react-icons/md';
import ConfirmDialog from '../../components/ConfirmDialog';
import { ApiKeyDto } from '../../models/api-key/dto/api-key.dto';
import { CreateApiKeyResponseDto } from '../../models/api-key/dto/create-api-key.dto';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import { ApiKeyTable } from './components/ApiKeyTable';
import { CreateApiKeyModal } from './components/CreateApiKeyModal';
import { NewApiKeySuccessModal } from './components/NewApiKeySuccessModal';
import { copy } from './copy';
import { useGetApiKeys } from './hooks/useGetApiKeys';
import { useGetScopes } from './hooks/useGetScopes';
import { useRevokeApiKey } from './hooks/useRevokeApiKey';

const MAX_ACTIVE_KEYS = 20;

export default function ApiKeysPage() {
  const { selectedOrganization } = useSelectedOrganization();
  const organizationId = selectedOrganization?.id ?? '';

  const [includeRevoked, setIncludeRevoked] = useState(false);
  const createModal = useDisclosure();
  const successModal = useDisclosure();
  const [successResult, setSuccessResult] =
    useState<CreateApiKeyResponseDto | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyDto | null>(null);
  const revokeDialog = useDisclosure();
  const toast = useToast();

  const keysQuery = useGetApiKeys(organizationId, includeRevoked);
  const scopesQuery = useGetScopes(organizationId);
  const revokeMutation = useRevokeApiKey(organizationId);

  const keys = keysQuery.data?.apiKeys ?? [];
  const scopes = scopesQuery.data?.scopes ?? [];

  const { activeCount, revokedCount } = useMemo(() => {
    let active = 0;
    let revoked = 0;
    for (const k of keys) {
      if (k.revokedAt) revoked++;
      else active++;
    }
    return { activeCount: active, revokedCount: revoked };
  }, [keys]);

  const atCap = activeCount >= MAX_ACTIVE_KEYS;

  const onCreateSuccess = (result: CreateApiKeyResponseDto) => {
    createModal.onClose();
    setSuccessResult(result);
    successModal.onOpen();
  };

  const onRevokeConfirm = async () => {
    if (!revokeTarget) return;
    try {
      await revokeMutation.mutateAsync(revokeTarget.id);
      toast({
        title: copy.revokedToast(revokeTarget.name),
        status: 'info',
        duration: 3500,
      });
    } catch (error) {
      toast({
        title: copy.revokeErrorToast,
        description: (error as Error).message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      revokeDialog.onClose();
      setRevokeTarget(null);
    }
  };

  if (!organizationId) {
    return (
      <PageCard title={copy.pageTitle}>
        <Box px={6} py={8}>
          <Alert status="info" borderRadius="8px">
            <AlertIcon />
            {copy.selectOrgPrompt}
          </Alert>
        </Box>
      </PageCard>
    );
  }

  return (
    <>
      <PageCard
        title={copy.pageTitle}
        rightHeader={
          <Tooltip
            label={atCap ? copy.createDisabledTooltip(MAX_ACTIVE_KEYS) : ''}
            placement="bottom-end"
            hasArrow
            isDisabled={!atCap}
          >
            <Button
              leftIcon={<MdAdd />}
              colorScheme="brand"
              onClick={createModal.onOpen}
              isDisabled={atCap || keysQuery.isLoading}
            >
              {copy.createButton}
            </Button>
          </Tooltip>
        }
      >
        <Box px={6} pb={2}>
          <Text fontSize="14px" color="gray.600" mb={4}>
            {copy.pageSubtitle}
          </Text>
          {revokedCount > 0 && (
            <Flex mb={3}>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setIncludeRevoked((v) => !v)}
              >
                {includeRevoked
                  ? copy.hideRevoked
                  : copy.showRevoked(revokedCount)}
              </Button>
            </Flex>
          )}
        </Box>

        {keysQuery.isLoading ? (
          <Center py={16}>
            <Spinner />
          </Center>
        ) : keysQuery.isError ? (
          <Box px={6} pb={8}>
            <Alert status="error" borderRadius="8px">
              <AlertIcon />
              <Flex direction="column" flex={1}>
                <Text fontWeight={600}>{copy.errorHeadline}</Text>
                <Text fontSize="13px">
                  {(keysQuery.error as Error)?.message ?? ''}
                </Text>
              </Flex>
              <Button
                size="sm"
                variant="outline"
                onClick={() => keysQuery.refetch()}
              >
                {copy.errorCta}
              </Button>
            </Alert>
          </Box>
        ) : keys.length === 0 ? (
          <EmptyState onCreate={createModal.onOpen} />
        ) : (
          <ApiKeyTable
            apiKeys={keys}
            scopes={scopes}
            onRevoke={(apiKey) => {
              setRevokeTarget(apiKey);
              revokeDialog.onOpen();
            }}
          />
        )}
      </PageCard>

      <CreateApiKeyModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        organizationId={organizationId}
        scopes={scopes}
        onSuccess={onCreateSuccess}
      />

      <NewApiKeySuccessModal
        isOpen={successModal.isOpen}
        plaintext={successResult?.plaintext ?? null}
        keyName={successResult?.apiKey.name ?? 'key'}
        onClose={() => {
          successModal.onClose();
          setSuccessResult(null);
        }}
      />

      <ConfirmDialog
        isOpen={revokeDialog.isOpen}
        onClose={() => {
          revokeDialog.onClose();
          setRevokeTarget(null);
        }}
        onConfirm={onRevokeConfirm}
        title={copy.revokeConfirm.title}
        body={
          revokeTarget
            ? `${copy.revokeConfirm.body}\n\n"${revokeTarget.name}"`
            : copy.revokeConfirm.body
        }
        confirmLabel={copy.revokeConfirm.confirm}
        cancelLabel={copy.revokeConfirm.cancel}
        intent="danger"
        isConfirming={revokeMutation.isLoading}
      />
    </>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <Center py={16} px={6}>
      <Flex direction="column" align="center" maxW="400px" textAlign="center">
        <Flex
          w="80px"
          h="80px"
          borderRadius="full"
          bg="blackAlpha.50"
          align="center"
          justify="center"
          mb={4}
        >
          <Icon as={MdVpnKey} boxSize="32px" color="brand.500" />
        </Flex>
        <Text
          fontFamily="'Instrument Serif', Georgia, serif"
          fontStyle="italic"
          fontSize="22px"
          mb={3}
        >
          {copy.emptyHeadline}
        </Text>
        <Text fontSize="14px" color="gray.600" mb={6}>
          {copy.emptyBody}
        </Text>
        <Button colorScheme="brand" onClick={onCreate}>
          {copy.emptyCta}
        </Button>
      </Flex>
    </Center>
  );
}
